// The data layer: one thin wrapper per thing a screen needs.
//
// This used to be 1,600 lines of query authorship — OData filter strings,
// 25-item OR-list chunking, skip-token paging, `$select` column lists, and a
// result envelope to unwrap. All of it is gone: the API takes named, typed
// parameters, pages with opaque cursors, and returns rows or throws. What is
// left here is naming and shaping, so screens read the same as before.
//
// Errors are `ApiError` (src/api/errors.ts) and are allowed to propagate —
// screens already render `error.message`, and the API guarantees that text is
// human-readable and free of protected health information.
import { getClient } from '../api/client'
import { ApiError } from '../api/errors'
import type {
  Account,
  AnalyticsAging,
  AnalyticsCorrections,
  AnalyticsDenials,
  AnalyticsOverview,
  AnalyticsParams,
  AnalyticsSpendByEntity,
  AnalyticsThroughput,
  Assignment,
  AssignmentRequest,
  AssignmentResult,
  AssociatedEntity,
  InvoiceLineItem,
  InvoiceLineItemWrite,
  Company,
  CompanyEmailDomain,
  CompanyScanner,
  IntakeSender,
  Conversation,
  Correction,
  Decision,
  DecisionBatchRequest,
  DecisionBatchResult,
  DecisionRequest,
  DecisionResult,
  DirectoryUser,
  ExportBatch,
  ExportDetail,
  ExportFile,
  Flag,
  Hold,
  IntakeSaveRequest,
  IntakeSaveResult,
  Invoice,
  InvoiceBundle,
  InvoiceInclude,
  ReminderSubscription,
  ReminderWrite,
  Upload,
  UserRole,
  UserRoleToken,
  Vehicle,
  VehicleImportPreview,
  VehicleImportResult,
  Vendor,
  VendorCodingRule,
  CostCenter,
  ExpenseType,
} from '../api/types'
import { filterToParams, type InvoiceFilter } from './filters'
import type { InvoiceStatus } from './constants'

const api = () => getClient()

// ---- Invoices ---------------------------------------------------------------

/** Every invoice at one of these statuses, oldest-first — the queue read. */
export async function loadInvoicesByStatus(statuses: readonly string[]): Promise<Invoice[]> {
  const page = await api().listInvoices({
    status: statuses as InvoiceStatus[],
    sort: 'invoice_date_asc',
    view: 'full',
    limit: 200,
  })
  return page.data
}

/** The queue column set: enough for a card, its badges and its filters. */
export async function loadQueueRows(statuses: readonly string[]): Promise<Invoice[]> {
  const page = await api().listInvoices({
    status: statuses as InvoiceStatus[],
    sort: 'invoice_date_asc',
    view: 'queue',
    limit: 200,
  })
  return page.data
}

export async function getInvoice(invoiceId: string): Promise<Invoice> {
  const bundle = await api().getInvoice(invoiceId)
  return bundle.invoice
}

/** The detail modal's one round trip: header plus whichever satellites it shows. */
export function getInvoiceBundle(invoiceId: string, include: InvoiceInclude[]): Promise<InvoiceBundle> {
  return api().getInvoice(invoiceId, include)
}

export interface ArchivePage {
  rows: Invoice[]
  /** Opaque — pass it back for the next page; never inspect it. */
  cursor?: string
}

/** Archives pages server-side, and filters there too, so filtering and paging
    compose. The client passes named parameters; it never composes a query. */
export async function loadArchivePage(
  statuses: readonly string[],
  filter: InvoiceFilter,
  cursor?: string,
  pageSize = 50,
): Promise<ArchivePage> {
  const page = await api().listInvoices({
    ...filterToParams(filter),
    status: statuses as InvoiceStatus[],
    sort: 'invoice_date_desc',
    view: 'archive',
    limit: pageSize,
    cursor,
  })
  return { rows: page.data, cursor: page.next_cursor ?? undefined }
}

/** The invoices in one export batch at a given status (Closing Confirmation). */
export async function loadInvoicesByExportKey(exportId: string, status: string): Promise<Invoice[]> {
  const page = await api().listInvoices({
    export_id: exportId,
    status: [status as InvoiceStatus],
    view: 'full',
    limit: 200,
  })
  return page.data
}

/** The Import Control save. One request, one transaction: header fields, line
    upserts and removals, flag auto-resolution, the correction rows, and — with
    `release` — the release gates and the Import Control approve itself. */
export function intakeSave(invoiceId: string, body: IntakeSaveRequest): Promise<IntakeSaveResult> {
  return api().intakeSave(invoiceId, body)
}

// ---- Line items ----------------------------------------------------------------

export function loadLineItems(invoiceId: string): Promise<InvoiceLineItem[]> {
  return api().listLineItems(invoiceId)
}

/** Lines for many invoices at once. The API takes the id list; the old client
    chopped it into 25-id batches because the request URL overflowed. */
export function loadLineItemsForInvoices(invoiceIds: string[]): Promise<InvoiceLineItem[]> {
  return api().listLineItemsForInvoices(invoiceIds)
}

export function createLineItem(invoiceId: string, record: InvoiceLineItemWrite): Promise<InvoiceLineItem> {
  return api().createLineItem(invoiceId, record)
}

export function updateLineItem(invoiceId: string, lineItemId: string, changes: InvoiceLineItemWrite): Promise<InvoiceLineItem> {
  return api().updateLineItem(invoiceId, lineItemId, changes)
}

/** Removing a line is a soft delete — the row persists as audit. */
export function deactivateLineItem(invoiceId: string, lineItemId: string, rowVersion: number): Promise<void> {
  return api().deleteLineItem(invoiceId, lineItemId, rowVersion)
}

/** The COMPOSED account write — Export Control and Manual Entry. The server
    decomposes it into both halves in one transaction. Authority (which step,
    which company) and the correction row are both the server's business; this
    never throws, so a refusal reads as `{ applied: false }` with the API's own
    wording. */
export async function applyLineItemAccountEdit(
  invoiceId: string,
  lineItemId: string,
  accountId: string,
  rowVersion?: number,
): Promise<{ applied: boolean; reason?: string; lineItem?: InvoiceLineItem }> {
  try {
    const res = await api().setLineItemAccount(invoiceId, lineItemId, {
      account_id: accountId,
      row_version: rowVersion,
    })
    return { applied: true, lineItem: res.line_item }
  } catch (e) {
    if (e instanceof ApiError) return { applied: false, reason: e.message }
    throw e
  }
}

/**
 * "What is it?" — the half-write, and the only way an approval tier moves the
 * expense type. The server re-applies the composition rule to the cost center,
 * so read the returned line rather than assuming the other half held.
 */
export async function applyLineItemExpenseTypeEdit(
  invoiceId: string,
  lineItemId: string,
  expenseTypeCode: string | null,
  rowVersion?: number,
): Promise<{ applied: boolean; reason?: string; lineItem?: InvoiceLineItem }> {
  try {
    const res = await api().setLineItemExpenseType(invoiceId, lineItemId, {
      expense_type_code: expenseTypeCode,
      row_version: rowVersion,
    })
    return { applied: true, lineItem: res.line_item }
  } catch (e) {
    if (e instanceof ApiError) return { applied: false, reason: e.message }
    throw e
  }
}

/**
 * "Who is it for?" — the half-write, from Department Approval onward. Refused
 * with the server's own wording when the pair does not compose (422) or the
 * step, the hold or the assignment says no.
 */
export async function applyLineItemCostCenterEdit(
  invoiceId: string,
  lineItemId: string,
  costCenterCode: string | null,
  rowVersion?: number,
): Promise<{ applied: boolean; reason?: string; lineItem?: InvoiceLineItem }> {
  try {
    const res = await api().setLineItemCostCenter(invoiceId, lineItemId, {
      cost_center_code: costCenterCode,
      row_version: rowVersion,
    })
    return { applied: true, lineItem: res.line_item }
  } catch (e) {
    if (e instanceof ApiError) return { applied: false, reason: e.message }
    throw e
  }
}

/**
 * "Who is it for?" across a SELECTION of one invoice's lines — §9.3's bulk
 * apply. One cost center, one transaction, one correction per line that moved.
 *
 * Approval is refused while any line lacks a cost center, and no incoming
 * invoice carries a handwritten GL code any more, so most invoices arrive
 * uncoded on this half. Without this a twelve-line invoice is twelve saves.
 *
 * `lineNumbers` is populated on the 422: the server names exactly which lines
 * do not compose, so the caller can deselect them rather than lose the
 * selection. Nothing is written on any refusal — it is all-or-nothing.
 */
export async function applyLineItemCostCentersBulk(
  invoiceId: string,
  selection: readonly { id: string; row_version?: number | null }[],
  costCenterCode: string,
): Promise<{
  applied: boolean
  reason?: string
  lineNumbers?: number[]
  lineItems?: InvoiceLineItem[]
  correctionsWritten?: number
}> {
  try {
    const res = await api().setLineItemCostCentersBulk(invoiceId, {
      cost_center_code: costCenterCode,
      line_items: selection.map((line) => ({
        invoice_line_item_id: line.id,
        // Every line-item write on this API is a compare-and-set; the server
        // requires the version rather than defaulting it.
        row_version: Number(line.row_version ?? 1),
      })),
    })
    return {
      applied: true,
      lineItems: res.line_items,
      correctionsWritten: res.correction_ids.length,
    }
  } catch (e) {
    if (e instanceof ApiError) {
      const numbers = e.details?.['line_numbers']
      return {
        applied: false,
        reason: e.message,
        ...(Array.isArray(numbers) ? { lineNumbers: numbers.map(Number) } : {}),
      }
    }
    throw e
  }
}

/**
 * §9.3's option list: the INTERSECTION of `costCentersFor` across the selected
 * lines, because one cost center has to compose with every one of them.
 *
 * An EMPTY result is a real answer and the control must say so rather than
 * render an empty dropdown — 91% of the possible cost-center × expense-type
 * combinations are not accounts, so a mixed selection genuinely often has
 * nothing in common. A line with NO expense type composes with nothing, so a
 * selection containing one is empty by construction: there is no account to
 * reach from it, and offering any cost center would be a lie.
 */
export async function loadBulkCostCenterOptions(
  expenseTypeCodes: readonly (string | null | undefined)[],
): Promise<CostCenter[]> {
  if (expenseTypeCodes.length === 0) return []
  if (expenseTypeCodes.some((code) => !code)) return []
  const distinct = [...new Set(expenseTypeCodes as string[])]
  const lists = await Promise.all(
    distinct.map((code) => api().listCostCenters({ active: true, expense_type_code: code, limit: MASTER_PAGE_LIMIT })),
  )
  const [first, ...rest] = lists
  if (first === undefined) return []
  const common = new Set(first.map((c) => c.cost_center_code))
  for (const list of rest) {
    const here = new Set(list.map((c) => c.cost_center_code))
    for (const code of [...common]) if (!here.has(code)) common.delete(code)
  }
  return first.filter((c) => common.has(c.cost_center_code))
}

// ---- Flags ------------------------------------------------------------------

export function loadFlags(invoiceId: string): Promise<Flag[]> {
  return api().listFlags(invoiceId)
}

export function loadUnresolvedFlags(invoiceId: string): Promise<Flag[]> {
  return api().listFlags(invoiceId, { unresolved: true })
}

/** A queue row already carries its unresolved flag types, so the chips need no
    second read — this is the accessor, not a fetch. */
export function unresolvedFlagTypes(invoice: Invoice): string[] {
  return invoice.flag_types ?? []
}

/** "Mark resolved". Deliberately writes no correction row: a flag cleared with
    zero corrections is the false-positive signal for flag precision. */
export function resolveFlag(
  invoiceId: string,
  flagId: string,
): Promise<{ flag: Flag; unresolved_flag_count: number }> {
  return api().resolveFlag(invoiceId, flagId)
}

// ---- Decisions --------------------------------------------------------------

export function loadDecisions(invoiceId: string): Promise<Decision[]> {
  return api().listDecisions(invoiceId)
}

/** Every status transition. Synchronous: the response carries the new status,
    so there is nothing to poll and nothing to guess. A refusal throws with the
    reason (`Blocked by an active hold`, `Out of order — record status is …`,
    `Not authorized for this step`) and writes nothing at all. */
export function createDecision(body: DecisionRequest): Promise<DecisionResult> {
  return api().createDecision(body)
}

/** Bulk approve / deny / Confirm Batch. Always resolves: each invoice is its
    own transaction and each result says whether it applied and why not. */
export function createDecisionBatch(body: DecisionBatchRequest): Promise<DecisionBatchResult> {
  return api().createDecisionBatch(body)
}

// ---- Holds ------------------------------------------------------------------

/** The active hold per invoice, for the rows that have one. The queue row's
    `has_active_hold` says which, so this reads only those. */
export async function loadActiveHolds(invoices: readonly Invoice[]): Promise<Record<string, Hold>> {
  const held = invoices.filter((i) => i.has_active_hold)
  if (held.length === 0) return {}
  const results = await Promise.all(
    held.map(async (i) => [i.id, (await api().listHolds(i.id, { active: true }))[0]] as const),
  )
  const map: Record<string, Hold> = {}
  for (const [id, hold] of results) if (hold) map[id] = hold
  return map
}

export function loadHolds(invoiceId: string): Promise<Hold[]> {
  return api().listHolds(invoiceId, { active: true })
}

export function createHold(invoiceId: string, reason: string): Promise<Hold> {
  return api().createHold(invoiceId, { reason })
}

export function releaseHold(holdId: string): Promise<Hold> {
  return api().releaseHold(holdId)
}

// ---- Comment thread ---------------------------------------------------------

export function loadConversations(invoiceId: string): Promise<Conversation[]> {
  return api().listConversations(invoiceId)
}

/** The server stamps the author and the record's company, so a comment's
    visibility mirrors its record without the client naming anything. */
export function createConversation(
  invoiceId: string,
  body: string,
  mentions: string[],
): Promise<{ conversation: Conversation; notifications_queued: number }> {
  return api().createConversation(invoiceId, { body, mentions })
}

// ---- Associated entities ----------------------------------------------------

export function loadAssociatedEntities(invoiceId: string): Promise<AssociatedEntity[]> {
  return api().listAssociatedEntities(invoiceId)
}

export function createAssociatedEntity(
  invoiceId: string,
  record: Partial<AssociatedEntity>,
): Promise<AssociatedEntity> {
  return api().createAssociatedEntity(invoiceId, record)
}

export function updateAssociatedEntity(
  id: string,
  changes: Partial<AssociatedEntity>,
): Promise<AssociatedEntity> {
  return api().updateAssociatedEntity(id, changes)
}

export function deactivateAssociatedEntity(id: string, rowVersion: number): Promise<void> {
  return api().deleteAssociatedEntity(id, rowVersion)
}

// ---- Corrections (read-only; the server writes them) ------------------------

export async function loadCorrectionsByIds(invoiceIds: readonly string[]): Promise<Correction[]> {
  if (invoiceIds.length === 0) return []
  const page = await api().listCorrections({ parent_ids: [...invoiceIds], limit: 200 })
  return page.data
}

// ---- Uploads ----------------------------------------------------------------

export function getUpload(uploadId: string): Promise<Upload> {
  return api().getUpload(uploadId)
}

/** A short-lived signed URL for the source document. The endpoint checks the
    caller's own document access before issuing one — that check is the only
    gate now, so it is load-bearing. */
export function getUploadPdfUrl(
  uploadId: string,
): Promise<{ url: string; expires_at: string; file_name?: string | null }> {
  return api().getUploadPdfUrl(uploadId)
}

/** Uploads one file: ask for a place to put it, put it there, then register it.
    The registration key is required and idempotent, so a retry cannot create a
    second row or a second extraction charge. */
export async function uploadDocument(file: File): Promise<Upload> {
  const client = api()
  const presigned = await client.presignUpload({
    file_name: file.name,
    content_type: file.type || 'application/pdf',
    byte_size: file.size,
    expected_type: 'invoice',
  })
  await client.putUploadFile(presigned, file)
  return client.registerUpload(
    {
      upload_id: presigned.upload_id,
      object_key: presigned.object_key,
      file_name: file.name,
      expected_type: 'invoice',
    },
    presigned.upload_id,
  )
}

// ---- Exports ----------------------------------------------------------------

export function loadRecentExports(top = 10): Promise<ExportBatch[]> {
  return api().listExports({ limit: top })
}

/** Approve & Export. One call builds every per-company file, writes the copy of
    record, stamps the batch on each invoice and writes each Export Control
    approval — or fails and writes nothing. The file contents are the server's:
    Point Click Care parses those bytes and the format is immutable. */
export function createExport(
  invoiceIds: string[],
): Promise<{ export: ExportBatch; files: ExportFile[]; invoices_advanced: number }> {
  return api().createExport(invoiceIds)
}

export function getExport(exportId: string): Promise<ExportDetail> {
  return api().getExport(exportId)
}

export function getExportFileUrl(
  exportId: string,
  fileId: string,
): Promise<{ url: string; file_name?: string | null }> {
  return api().getExportFileUrl(exportId, fileId)
}

// ---- Reminder subscriptions -------------------------------------------------

export function loadMyReminders(): Promise<ReminderSubscription[]> {
  return api().listReminderSubscriptions()
}

export function createReminder(record: ReminderWrite): Promise<ReminderSubscription> {
  return api().createReminderSubscription(record)
}

/** Editing is create-then-supersede, in one server transaction, carrying the
    last-sent stamp forward so a mid-day edit cannot double-send. */
export function replaceReminder(
  id: string,
  record: ReminderWrite & { row_version: number },
): Promise<{ subscription: ReminderSubscription; superseded_id: string }> {
  return api().replaceReminderSubscription(id, record)
}

export function setReminderStatus(
  id: string,
  status: 'active' | 'paused' | 'cancelled',
): Promise<ReminderSubscription> {
  return api().setReminderSubscriptionStatus(id, status)
}

// ---- Assignments ------------------------------------------------------------

export async function listAssignments(): Promise<Assignment[]> {
  const page = await api().listAssignments({ workspace: 'invoice', limit: 200 })
  return page.data
}

/** Appoint or retire. Synchronous, and the row is written already applied —
    there is no pending request to poll for. */
export function createAssignment(body: AssignmentRequest): Promise<AssignmentResult> {
  return api().createAssignment(body)
}

// ---- Internal step roles ----------------------------------------------------

export function listUserRoles(): Promise<UserRole[]> {
  return api().listUserRoles({ workspace: 'invoice' })
}

export function grantUserRole(body: {
  role: UserRoleToken
  user_id: string
  user_upn: string
  user_name?: string | null
  /** The directory mail address when Graph returns one; the server falls back
      to the UPN, which is what mentions and digests then compare against. */
  user_email?: string | null
}): Promise<UserRole> {
  return api().createUserRole({ workspace: 'invoice', ...body })
}

export function revokeUserRole(userRoleId: string): Promise<void> {
  return api().deleteUserRole(userRoleId)
}

// ---- Pickers ----------------------------------------------------------------
// Every picker searches server-side now. The old client loaded the whole chart
// of accounts and the whole fleet into memory and filtered there, because the
// Dataverse data source silently ignored a `$filter` on those columns.

/** Option shape the shared EntityPicker consumes (id + display text). */
export interface PickerOption {
  id: string
  primary: string
  secondary?: string
}

export async function searchVendors(term: string, top = 50): Promise<Vendor[]> {
  const page = await api().listVendors({ q: term || undefined, active: true, limit: top })
  return page.data
}

export async function searchCompanies(term: string, top = 100): Promise<Company[]> {
  const page = await api().listCompanies({ q: term || undefined, active: true, limit: top })
  return page.data
}

export async function searchAccounts(term: string, top = 50): Promise<Account[]> {
  const page = await api().listAccounts({ q: term || undefined, active: true, limit: top })
  return page.data
}

/** "What is it?" — the picker every coding surface offers. */
export function searchExpenseTypes(term: string): Promise<ExpenseType[]> {
  return api().listExpenseTypes({ q: term || undefined, active: true })
}

/**
 * "Who is it for?" — ONE-DIRECTIONAL: the options derive from the expense type,
 * never the reverse. Passing no expense type offers every active cost center,
 * which is only right where no expense type is settled yet.
 */
export function searchCostCenters(term: string, expenseTypeCode?: string): Promise<CostCenter[]> {
  return api().listCostCenters({
    q: term || undefined,
    active: true,
    ...(expenseTypeCode === undefined ? {} : { expense_type_code: expenseTypeCode }),
  })
}

// ---- Half labels ------------------------------------------------------------
//
// A line item carries CODES — `67-502`, `689359` — and nothing else. The label
// is master data, so every surface that shows a half has to resolve it, and a
// surface that does not shows an empty box on a line that is in fact coded.
//
// Both masters are small and static (82 expense types, 33 cost centers), so this
// reads each once per session and memoizes the promise. Two deliberate choices:
//
//  - `limit: MASTER_PAGE_LIMIT`, because the API's default page is 50 and there
//    are 82 expense types. A default page would silently drop a third of them and
//    the symptom would be an empty box on exactly the lines nobody looked at.
//  - NO `active` filter, unlike the pickers. A line may hold a half that was
//    retired under it; that line still has to render and still has to export,
//    the same reason the server's name-repopulation runs with `activeOnly:
//    false`. Offering a retired half is the picker's problem, not the label's.

/** The API's `MAX_PAGE_LIMIT`. Restated here because the SPA deliberately does
    not depend on `@ap/shared` — it keeps a hand-written wire twin. */
const MASTER_PAGE_LIMIT = 200

let expenseTypeLabelsOnce: Promise<ReadonlyMap<string, string>> | null = null
let costCenterLabelsOnce: Promise<ReadonlyMap<string, string>> | null = null

/** Expense type code -> label, for display. Read once, memoized. */
export function loadExpenseTypeLabels(): Promise<ReadonlyMap<string, string>> {
  expenseTypeLabelsOnce ??= api()
    .listExpenseTypes({ limit: MASTER_PAGE_LIMIT })
    .then((rows) => new Map(rows.map((e) => [e.expense_type_code, e.label])))
    .catch((e: unknown) => {
      // A failed read must not poison the cache for the rest of the session.
      expenseTypeLabelsOnce = null
      throw e
    })
  return expenseTypeLabelsOnce
}

/** Cost center code -> label, for display. Read once, memoized. */
export function loadCostCenterLabels(): Promise<ReadonlyMap<string, string>> {
  costCenterLabelsOnce ??= api()
    .listCostCenters({ limit: MASTER_PAGE_LIMIT })
    .then((rows) => new Map(rows.map((c) => [c.cost_center_code, c.label])))
    .catch((e: unknown) => {
      costCenterLabelsOnce = null
      throw e
    })
  return costCenterLabelsOnce
}

/** Drop both caches. The mock harness reseeds between tests; nothing else needs it. */
export function resetHalfLabelCaches(): void {
  expenseTypeLabelsOnce = null
  costCenterLabelsOnce = null
}

/** Vehicles: the option id is the vehicles-row UUID, because that is what a
    `Vehicle` tag stores — `associated_entities.vehicle_id` is a foreign key to
    `vehicles.id` (api-contract §9), not the fleet number. The number is the
    DISPLAY value, and it is what the row's `entity_name` keeps. Sending the
    number as the id is the M9 defect: the API validates `vehicle_id` as a uuid,
    so the pick was refused or dropped and the tag saved unresolved. */
export async function searchVehicles(term: string, top = 50): Promise<PickerOption[]> {
  const page = await api().listVehicles({ q: term || undefined, active: true, limit: top })
  return page.data.map((v) => ({
    id: v.id,
    primary: v.number,
    secondary: [v.make, v.model, v.license_plate, v.vin].filter(Boolean).join(' · ') || undefined,
  }))
}

/** Staff: the id is the user principal name, because that is what entity
    resolution writes. The search is Microsoft Graph on the signed-in user's own
    token — the same directory the old connector read, and none of it is PHI. */
export async function searchStaff(term: string, top = 25): Promise<PickerOption[]> {
  const users = await api().searchDirectoryUsers(term, top)
  return users.map((u) => ({
    id: u.upn,
    primary: u.display_name || u.upn,
    secondary: [u.job_title, u.department].filter(Boolean).join(' · ') || u.mail || undefined,
  }))
}

/** People to appoint. Same directory search; an appointment carries the
    directory id and the user principal name. */
export function searchPeople(term: string, top = 25): Promise<DirectoryUser[]> {
  return api().searchDirectoryUsers(term, top)
}

// ---- Reference (master) data ------------------------------------------------
// Retire and reactivate are one PATCH of `is_active`, not separate verbs. A
// retired row persists as audit and drops out of every picker.

export async function loadVendorsForManagement(term?: string, limit = 50): Promise<Vendor[]> {
  const page = await api().listVendors({ q: term || undefined, limit })
  return page.data
}

export function createVendor(record: Partial<Vendor> & { vendor_id: string; vendor_name: string }): Promise<Vendor> {
  return api().createVendor(record)
}

export function updateVendor(
  vendorId: string,
  changes: Partial<Vendor> & { row_version: number },
): Promise<Vendor> {
  return api().updateVendor(vendorId, changes)
}

export function setVendorActive(vendorId: string, isActive: boolean, rowVersion: number): Promise<Vendor> {
  return api().updateVendor(vendorId, { is_active: isActive, row_version: rowVersion })
}

export async function loadCompaniesForManagement(term?: string): Promise<Company[]> {
  const page = await api().listCompanies({ q: term || undefined, limit: 200 })
  return page.data
}

export function createCompany(record: { company_id: string; company_name: string }): Promise<Company> {
  return api().createCompany(record)
}

/** A company may be renamed. The name captured onto an invoice at match time
    stays frozen as the accounting record and is never re-synced — authority and
    visibility key on the company id, so a rename moves nothing. */
export function updateCompany(
  companyId: string,
  changes: { company_name?: string; is_active?: boolean; row_version: number },
): Promise<Company> {
  return api().updateCompany(companyId, changes)
}

export function loadCompanyEmailDomains(companyId?: string): Promise<CompanyEmailDomain[]> {
  return api().listCompanyEmailDomains({ company_id: companyId })
}

/** Stored lowercased and with the leading `@` — that is the key the
    company-from-uploader-domain rung compares against. */
export function normalizeDomain(raw: string): string {
  const t = raw.trim().toLowerCase()
  if (!t) return ''
  return t.startsWith('@') ? t : `@${t}`
}

export function createCompanyEmailDomain(domain: string, companyId: string): Promise<CompanyEmailDomain> {
  return api().createCompanyEmailDomain({ domain: normalizeDomain(domain), company_id: companyId })
}

export function updateCompanyEmailDomain(
  id: string,
  changes: { company_id?: string; is_active?: boolean; row_version: number },
): Promise<CompanyEmailDomain> {
  return api().updateCompanyEmailDomain(id, changes)
}

export function loadCompanyScanners(companyId?: string): Promise<CompanyScanner[]> {
  return api().listCompanyScanners({ company_id: companyId })
}

/** Stored lowercased and trimmed — the key the scanner-fallback rung compares
    against the From header's display name, normalized the same way. */
export function normalizeScannerName(raw: string): string {
  return raw.trim().toLowerCase()
}

export function createCompanyScanner(scannerName: string, companyId: string): Promise<CompanyScanner> {
  return api().createCompanyScanner({
    scanner_name: normalizeScannerName(scannerName),
    company_id: companyId,
  })
}

export function updateCompanyScanner(
  id: string,
  changes: { company_id?: string; is_active?: boolean; row_version: number },
): Promise<CompanyScanner> {
  return api().updateCompanyScanner(id, changes)
}

export function loadIntakeSenders(): Promise<IntakeSender[]> {
  return api().listIntakeSenders()
}

export function createIntakeSender(address: string): Promise<IntakeSender> {
  return api().createIntakeSender({ address: address.trim().toLowerCase() })
}

export function updateIntakeSender(
  id: string,
  changes: { is_active?: boolean; row_version: number },
): Promise<IntakeSender> {
  return api().updateIntakeSender(id, changes)
}

export async function loadAccountsForManagement(term?: string): Promise<Account[]> {
  const page = await api().listAccounts({ q: term || undefined, limit: 200 })
  return page.data
}

export function createAccount(record: { account_id: string; account_name: string }): Promise<Account> {
  return api().createAccount(record)
}

export function updateAccount(
  accountId: string,
  changes: { account_name?: string; is_active?: boolean; row_version: number },
): Promise<Account> {
  return api().updateAccount(accountId, changes)
}

/** The two halves of an account id. Both lists are DERIVED from the chart. */
export function loadCostCenters(
  params: { q?: string; expense_type_code?: string } = {},
): Promise<CostCenter[]> {
  return api().listCostCenters({ ...params, active: true })
}

export function loadExpenseTypes(params: { q?: string } = {}): Promise<ExpenseType[]> {
  return api().listExpenseTypes({ ...params, active: true })
}

export function loadVendorCodingRules(
  params: { vendor_id?: string; expense_type_code?: string; cost_center_code?: string } = {},
): Promise<VendorCodingRule[]> {
  return api().listVendorCodingRules(params)
}

/**
 * A rule naming BOTH halves must compose a real account; the server refuses one
 * that does not, so this passes the halves through rather than pre-checking —
 * a pre-flight check the server then re-checks is a race.
 */
export function createVendorCodingRule(record: {
  vendor_id: string
  description_scope?: string | null
  expense_type_code?: string | null
  cost_center_code?: string | null
}): Promise<VendorCodingRule> {
  return api().createVendorCodingRule({ ...record, source: 'Controller' })
}

export function updateVendorCodingRule(
  id: string,
  changes: {
    description_scope?: string | null
    expense_type_code?: string | null
    cost_center_code?: string | null
    source?: 'Controller' | 'Learned'
    is_active?: boolean
    row_version: number
  },
): Promise<VendorCodingRule> {
  return api().updateVendorCodingRule(id, changes)
}

export async function loadVehiclesForManagement(term?: string): Promise<Vehicle[]> {
  const page = await api().listVehicles({ q: term || undefined, limit: 200 })
  return page.data
}

export function createVehicle(record: Partial<Vehicle> & { number: string }): Promise<Vehicle> {
  return api().createVehicle(record)
}

export function updateVehicle(
  vehicleId: string,
  changes: Partial<Vehicle> & { row_version: number },
): Promise<Vehicle> {
  return api().updateVehicle(vehicleId, changes)
}

/** The fleet spreadsheet is parsed on the server, so preview and commit cannot
    disagree about what the file said, and the browser never interprets a file.
    Absent vehicles are reported and never changed — a filtered export must not
    be able to retire half the fleet. */
export function previewVehicleImport(file: File): Promise<VehicleImportPreview> {
  return api().previewVehicleImport(file)
}

export function commitVehicleImport(previewToken: string): Promise<VehicleImportResult> {
  return api().commitVehicleImport(previewToken)
}

// ---- Analytics --------------------------------------------------------------
// Aggregates, computed by the API and scoped to what the caller may see. The
// old dashboard pulled every active invoice, decision, flag and correction into
// the browser to draw its bars.

export function loadAnalyticsOverview(params?: AnalyticsParams): Promise<AnalyticsOverview> {
  return api().getAnalyticsOverview(params)
}

export function loadAnalyticsAging(params?: AnalyticsParams): Promise<AnalyticsAging> {
  return api().getAnalyticsAging(params)
}

export function loadAnalyticsThroughput(params?: AnalyticsParams): Promise<AnalyticsThroughput> {
  return api().getAnalyticsThroughput(params)
}

export function loadAnalyticsDenials(params?: AnalyticsParams): Promise<AnalyticsDenials> {
  return api().getAnalyticsDenials(params)
}

export function loadAnalyticsSpendByEntity(params?: AnalyticsParams): Promise<AnalyticsSpendByEntity> {
  return api().getAnalyticsSpendByEntity(params)
}

export function loadAnalyticsCorrections(params?: AnalyticsParams): Promise<AnalyticsCorrections> {
  return api().getAnalyticsCorrections(params)
}

// ---- Queue counts -----------------------------------------------------------

/** Badge counts, one per node the caller can act on. Nodes they hold no role
    for are absent rather than zero, so the client cannot infer volumes it may
    not see. */
export async function loadQueueCounts(): Promise<Partial<Record<string, number>>> {
  const res = await api().getQueueCounts({ workspace: 'invoice' })
  return res.counts
}
